import { observer } from 'mobx-react-lite';
import { AriaProperties, AriaRoles, generateDomPropertiesForAria } from 'owa-accessibility';
import {
    getAccountSourceListStore,
    getGlobalSettingsAccountMailboxInfo,
} from 'owa-account-source-list-store';
import { isMonarchMultipleAccountsEnabled } from 'owa-account-source-list/lib/flights';
import loc, { format } from 'owa-localize';
import {
    favoritesHeaderText,
    favoritesWithAccountNameHeaderText,
} from 'owa-locstrings/lib/strings/favoritesheadertext.locstring.json';
import { lazyToggleFavoritesTreeExpansion } from 'owa-mail-favorites-store';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import TreeNode from 'owa-tree-node/lib/components/TreeNode';
import * as React from 'react';
import FavoritesList from './FavoritesList';
import { getStore as getSharedFavoritesStore, loadFavorites } from 'owa-favorites';
import { FolderTreeLoadStateEnum, getPrimaryMailFolders } from 'owa-folders';
import type { MailboxInfo } from 'owa-client-types';
import { getIndexerValueForMailboxInfo } from 'owa-client-types';

export interface FavoritesProps extends React.HTMLProps<HTMLDivElement> {
    setSize: number;
    positionInSet: number;
    mailboxInfo: MailboxInfo;
}

const Favorites = observer(function Favorites(props: FavoritesProps) {
    const ariaProps: AriaProperties = {
        role: AriaRoles.tree,
    };

    const isPrimaryAcount =
        getIndexerValueForMailboxInfo(props.mailboxInfo) ===
        getIndexerValueForMailboxInfo(getGlobalSettingsAccountMailboxInfo());
    const userIdentity = props.mailboxInfo.userIdentity;
    const key = getIndexerValueForMailboxInfo(props.mailboxInfo);

    const onClick = async (evt: React.MouseEvent<unknown>) => {
        evt.stopPropagation();
        // Toggle setting only for primary account
        // otherwise setIsTreeExpanded
        if (!isPrimaryAcount) {
            // We have to await so favorites is loaded correctly
            await getPrimaryMailFolders(false, userIdentity);

            // Show be a no-op if favorites are loaded
            loadFavorites(props.mailboxInfo);

            // Expand the tree
            setIsTreeExpanded(!isTreeExpanded);
        } else {
            lazyToggleFavoritesTreeExpansion.importAndExecute();
        }
    };

    const [isTreeExpanded, setIsTreeExpanded] = React.useState<boolean>(false);

    // We disable the context menu on the root.
    const onRootNodeContextMenu = (evt: React.MouseEvent<unknown>): void => {
        evt.preventDefault();
    };

    const renderFavoritesList = (): JSX.Element => {
        return <FavoritesList key="favoriteFolderList" mailboxInfo={props.mailboxInfo} />;
    };

    const getSelectedEmailAddress = () => {
        const accountSources = getAccountSourceListStore().sources;
        if (accountSources.length > 1 && isMonarchMultipleAccountsEnabled()) {
            return userIdentity;
        }

        return null;
    };

    const renderFavoriteRoot = (
        isFavoritesTreeExpanded: boolean,
        setSize: number,
        positionInSet: number
    ): JSX.Element => {
        const selectedEmailAddress = getSelectedEmailAddress();
        const displayName = selectedEmailAddress
            ? format(loc(favoritesWithAccountNameHeaderText), selectedEmailAddress)
            : loc(favoritesHeaderText);

        // We cannot use orderedFavoritesNodeIds here, because user might delete favorites on other client and the list hasn't been updated.
        // favoritesFolderNodes and favoritesPersonaNodes are the filtered nodes which represent the nodes that we could show.
        return (
            <TreeNode
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2322 (109,66): Type '(evt: MouseEvent<unknown, MouseEvent>) => void' is not assignable to type '(evt: MouseEvent<unknown, MouseEvent> | KeyboardEvent) => void'.
                // @ts-expect-error
                chevronProps={{ isExpanded: isFavoritesTreeExpanded, onClick: onClick }}
                displayName={displayName}
                depth={0}
                isRootNode={true}
                isSelected={false}
                key="favoritesRoot"
                onClick={onClick}
                onContextMenu={onRootNodeContextMenu}
                setSize={setSize}
                positionInSet={positionInSet}
            />
        );
    };

    let shouldShowTreeExpanded;
    // We will show the tree expanded/collapsed based on setting only for primary account
    // Primary accounts will always have favorites loaded as part of sessiondata
    // other accounts will be collapsed and favorites will loaded on demand
    if (!isPrimaryAcount) {
        shouldShowTreeExpanded =
            isTreeExpanded &&
            getSharedFavoritesStore().favoriteTreeData.get(key)?.loadingState ===
                FolderTreeLoadStateEnum.Loaded;
    } else {
        shouldShowTreeExpanded = !getUserConfiguration()?.UserOptions
            ?.IsFavoritesFolderTreeCollapsed;
    }

    return (
        <div
            style={props.style}
            className={props.className}
            {...generateDomPropertiesForAria(ariaProps)}>
            {renderFavoriteRoot(shouldShowTreeExpanded, props.setSize, props.positionInSet)}
            {shouldShowTreeExpanded && renderFavoritesList()}
        </div>
    );
});

export default Favorites;
